import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

/**
 * The languages the site ships in: the ASEAN working languages the buildings
 * sit in, plus Japanese and Korean for the region's inbound residents and
 * owners. Adding a locale is dropping
 * `public/locales/<code>/*.json` in place, listing the code here and giving it
 * a display name below — the detector, the backend loader, the `<html lang>`
 * sync and the language switcher all follow.
 *
 * Codes are base codes deliberately: `load: 'languageOnly'` folds the regional
 * tags a browser actually reports (`en-GB`, `id-ID`, `zh-CN`, `zh-SG`) onto
 * them, so one file per language serves every region that speaks it. `zh` is
 * Simplified — the script Singapore and the regional business audience read.
 */
export const supportedLanguages = [
  'en',
  'ms',
  'id',
  'th',
  'vi',
  'fil',
  'zh',
  'ja',
  'ko',
] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

/**
 * Endonyms, not English names. A resident scanning the switcher is looking for
 * the word they call their own language, not the word we call it.
 */
export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  ms: 'Bahasa Melayu',
  id: 'Bahasa Indonesia',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  fil: 'Filipino',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
};

/**
 * The three display faces (Outfit, Archivo, Manrope) are Latin-only, and
 * Outfit and Manrope do not ship a Vietnamese subset either, so anything past
 * unaccented Latin needs a face brought in with it. These are appended to the
 * font stacks by the `html[lang=…]` blocks in `index.css`, and fetched only
 * when a visitor actually switches to that language — an English reader never
 * pays for the Japanese face.
 */
const SCRIPT_FONTS: Partial<Record<SupportedLanguage, string>> = {
  th: 'Noto+Sans+Thai:wght@400;500;600;700',
  zh: 'Noto+Sans+SC:wght@400;500;600;700',
  ja: 'Noto+Sans+JP:wght@400;500;600;700',
  ko: 'Noto+Sans+KR:wght@400;500;600;700',
  vi: 'Noto+Sans:wght@400;500;600;700',
};

const loadedFonts = new Set<string>();

const ensureScriptFont = (lng: string) => {
  const family = SCRIPT_FONTS[lng as SupportedLanguage];
  if (!family || loadedFonts.has(family)) return;
  loadedFonts.add(family);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
  document.head.appendChild(link);
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: supportedLanguages as unknown as string[],
    // `en-SG` → `en`, `zh-Hans-CN` → `zh`. Without this every regional tag a
    // browser reports would miss `supportedLngs` and drop to the fallback.
    load: 'languageOnly',
    defaultNS: 'common',
    ns: ['common', 'home', 'management', 'auth'],

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'language',
      caches: ['localStorage'],
      // Some browsers still report Filipino under the older Tagalog code.
      convertDetectedLanguage: (lng: string) =>
        lng.toLowerCase().startsWith('tl') ? 'fil' : lng,
    },

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    react: {
      useSuspense: true,
    },

    interpolation: {
      escapeValue: false,
    },

    debug: import.meta.env.DEV,
  });

// Keep `<html lang>` and the script face in step with the active language.
i18n.on('languageChanged', (lng) => {
  document.documentElement.setAttribute('lang', lng);
  ensureScriptFont(lng);
});

ensureScriptFont(i18n.resolvedLanguage ?? 'en');

export default i18n;
